<template>
  <div id="myForm">
    <v-container class="fill-height">
      <v-responsive>
        <v-card v-if="!errorMessage">
          <v-card-title class="text-center">{{ statusTitle }}</v-card-title>
          <v-card-text class="text-center">
            <v-progress-circular class="mt-8" indeterminate></v-progress-circular>
            <div class="mt-8">{{ statusMessage }}</div>
          </v-card-text>
        </v-card>
        <v-card v-if="errorMessage">
          <v-card-title class="text-center">Login Error</v-card-title>
          <v-alert
            :text="errorMessage"
            title="Error with login"
            type="error"
          ></v-alert>
          <v-card-text class="text-center">
            <v-btn @click="retryLogin" class="mr-2">Try Again</v-btn>
            <v-btn v-if="escapeBotName" @click="openInNewWindow" color="secondary">
              Open in New Window
            </v-btn>
          </v-card-text>
        </v-card>
      </v-responsive>
    </v-container>
  </div>
</template>

<script>
import { routeGuardUtils } from "@/guards/casRouteGuard";
import { config } from "@/config/config";

export default {
  data() {
    return {
      statusTitle: 'Processing Login',
      statusMessage: 'Please wait...',
      errorMessage: null,
      escapeBotName: null
    };
  },
  async mounted() {
    let chatForm = document.getElementById("myForm");
    chatForm.style.width = "calc(100vw - 8px)";
    chatForm.style.height = "calc(100vh - 8px)";

    await this.handleLogin();
  },
  methods: {
    async handleLogin() {
      let botName = this.$route.query.botName;
      const ticket = this.$route.query.ticket;
      const isPopup = window.opener && !window.opener.closed;

      // For popup flow: Store botName in sessionStorage before redirecting to CAS
      if (botName && !ticket && isPopup) {
        sessionStorage.setItem('popupBotName', botName);
      }

      // If no botName in URL, try to retrieve it
      if (!botName) {
        if (isPopup) {
          // Popup: get from sessionStorage
          botName = sessionStorage.getItem('popupBotName');
        } else {
          // Normal flow: get from savedFullPath (stored before CAS redirect)
          const savedFullPath = localStorage.getItem("savedFullPath");
          if (savedFullPath) {
            try {
              const url = new URL(savedFullPath, window.location.origin);
              botName = new URLSearchParams(url.search).get('botName');
            } catch (e) {
              console.error('Error parsing savedFullPath:', e);
            }
          }
        }
      }

      // Build returnTo - ensure it has botName and clean it up
      let returnTo = this.$route.query.returnTo || `/?botName=${botName}`;

      // Clean ticket from returnTo if present
      try {
        const returnUrl = new URL(returnTo, window.location.origin);
        returnUrl.searchParams.delete('ticket');

        // Ensure botName is in returnTo
        if (!returnUrl.searchParams.get('botName') && botName) {
          returnUrl.searchParams.set('botName', botName);
        }

        returnTo = returnUrl.pathname + returnUrl.search + returnUrl.hash;
      } catch (e) {
        // If URL parsing fails, use default
        returnTo = `/?botName=${botName}`;
      }

      // console.log('Login page - botName:', botName, 'returnTo:', returnTo, 'ticket:', ticket);
      // console.log('Login page context - window.opener:', !!window.opener, 'inIframe:', window.self !== window.top);

      // Store botName for escape hatch
      this.escapeBotName = botName;

      // Check for missing botName
      if (!botName) {
        this.errorMessage = 'Bot name is required. Please ensure the chatbot URL includes ?botName=YOUR_BOT_NAME';
        console.error('No botName provided to login page');
        return;
      }

      // Priority 1: Am I a popup callback?
      // Must have window.opener AND a ticket AND botName to be popup callback mode
      if (isPopup && ticket && botName) {
        await this.handlePopupCallback(ticket, botName);
        return;
      }

      // Priority 2: Process ticket if present (fresh auth from CAS redirect)
      if (ticket) {
        await this.processTicketAndRedirect(ticket, botName, returnTo);
        return;
      }

      // Priority 3: Check if already authenticated
      if (this.hasValidJWT(botName)) {
        // console.log('Already authenticated, redirecting...');
        window.location = returnTo;
        return;
      }

      // Priority 4: Initiate login based on context
      await this.initiateLogin(botName, returnTo);
    },

    async handlePopupCallback(ticket, botName) {
      this.statusTitle = 'Processing Login';
      this.statusMessage = 'Authenticating...';

      try {
        // Process ticket
        const jwt = await routeGuardUtils.processTicket(ticket);

        if (!jwt) {
          this.statusMessage = 'Authentication failed.';
          console.error('Popup callback: Failed to get JWT from ticket');
          setTimeout(() => window.close(), 2000);
          return;
        }

        const decodedJwt = routeGuardUtils.decodeJWT(jwt);

        if (routeGuardUtils.isJwtTokenExpired(decodedJwt)) {
          this.statusMessage = 'Login token expired.';
          console.error('Popup callback: JWT is expired');
          setTimeout(() => window.close(), 2000);
          return;
        }

        this.statusMessage = 'Login successful! Closing...';

        // Send JWT + botName to parent window via postMessage
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({
            type: 'LOGIN_SUCCESS',
            botName: botName,
            jwt: jwt,
            username: decodedJwt.username
          }, window.location.origin);

          // Clean up sessionStorage
          sessionStorage.removeItem('popupBotName');

          setTimeout(() => {
            window.close();
          }, 1000);
        } else {
          console.warn('Popup callback: No valid parent window (mobile fallback)');
          this.statusMessage = 'Login successful! You can close this window.';
        }
      } catch (error) {
        console.error('Popup callback error:', error);
        this.statusMessage = 'An error occurred. Please try again.';
        setTimeout(() => window.close(), 2000);
      }
    },

    async processTicketAndRedirect(ticket, botName, returnTo) {
      this.statusTitle = 'Authenticating';
      this.statusMessage = 'Processing authentication...';

      try {
        const jwt = await routeGuardUtils.processTicket(ticket);

        if (!jwt) {
          throw new Error('Failed to obtain authentication token');
        }

        const decodedJwt = routeGuardUtils.decodeJWT(jwt);

        if (routeGuardUtils.isJwtTokenExpired(decodedJwt)) {
          throw new Error('Authentication token has expired');
        }

        // Store JWT
        localStorage.setItem("jwt-" + botName, jwt);
        localStorage.setItem("username", decodedJwt.username);
        this.clearLoginAttempts();

        // console.log('Authentication successful, redirecting to:', returnTo);
        window.location = returnTo;
      } catch (error) {
        console.error('Error processing ticket:', error);
        this.handleLoginFailure(error.message, botName, returnTo);
      }
    },

    async initiateLogin(botName, returnTo) {
      // Check if we're in an iframe
      const inIframe = window.self !== window.top;

      if (inIframe) {
        await this.handleIframeLogin(botName, returnTo);
      } else {
        this.handleNormalLogin(botName, returnTo);
      }
    },

    async handleIframeLogin(botName, returnTo) {
      this.statusTitle = 'Login Required';
      this.statusMessage = 'Requesting storage access...';

      // Request storage access for iframe
      try {
        await document.requestStorageAccess();
        // console.log('Storage access granted');
      } catch (err) {
        console.error('Storage access denied:', err);
        this.errorMessage = 'Storage access is required for login in embedded mode. Please allow storage access and try again.';
        return;
      }

      this.statusMessage = 'Opening login window...';

      let popup = null;
      let checkClosedInterval = null;
      let timeoutId = null;

      // Set up message listener for popup callback
      const messageHandler = (event) => {
        if (event.origin === window.location.origin && event.data.type === 'LOGIN_SUCCESS') {
          // Use botName from message (popup sends it explicitly)
          const messageBotName = event.data.botName;

          if (event.data.jwt && event.data.username && messageBotName) {
            // IMMEDIATELY clear intervals/timeouts before redirect
            if (checkClosedInterval) clearInterval(checkClosedInterval);
            if (timeoutId) clearTimeout(timeoutId);
            window.removeEventListener('message', messageHandler);
            if (popup && !popup.closed) popup.close();

            // Store auth data
            localStorage.setItem("jwt-" + messageBotName, event.data.jwt);
            localStorage.setItem("username", event.data.username);
            this.clearLoginAttempts();

            // Redirect
            window.location = returnTo;
          }
        }
      };

      // Cleanup function for error cases
      const cleanup = () => {
        if (checkClosedInterval) clearInterval(checkClosedInterval);
        if (timeoutId) clearTimeout(timeoutId);
        window.removeEventListener('message', messageHandler);
        if (popup && !popup.closed) {
          popup.close();
        }
      };

      window.addEventListener('message', messageHandler);

      // Open popup with botName in URL (popup will store it in sessionStorage)
      try {
        const popupUrl = `${window.location.origin}${window.location.pathname}#/login?botName=${encodeURIComponent(botName)}`;
        popup = window.open(popupUrl, 'CAS Login', 'width=640,height=480,scrollbars=yes');

        if (!popup) {
          throw new Error('Popup blocked');
        }

        this.statusMessage = 'Please complete login in the popup window...';

        // Strategy 1: Detect popup close
        checkClosedInterval = setInterval(() => {
          if (popup.closed) {
            cleanup();
            this.statusMessage = '';
            this.errorMessage = 'Login popup was closed. Please try again.';
          }
        }, 500);

        // Strategy 2: Timeout after 2 minutes
        timeoutId = setTimeout(() => {
          cleanup();
          this.statusMessage = '';
          this.errorMessage = 'Login timed out after 2 minutes. Please try again.';
        }, 120000);

      } catch (error) {
        console.error('Error opening popup:', error);
        cleanup();
        this.errorMessage = 'Failed to open login popup. Please enable popups for this site and try again.';
      }
    },

    popupCenter(url, title, w, h) {
      const features = `width=${w},height=${h},scrollbars=yes`;
      const newWindow = window.open(url, title, features);

      if (newWindow && window.focus) {
        newWindow.focus();
      }

      return newWindow;
    },

    handleNormalLogin(botName, returnTo) {
      this.statusTitle = 'Redirecting to Login';
      this.statusMessage = 'Redirecting to authentication service...';

      const attempts = this.getLoginAttempts();
      if (attempts >= 3) {
        this.errorMessage = 'Login failed after 3 attempts. Please refresh the page to try again.';
        this.clearLoginAttempts();
        return;
      }

      this.incrementLoginAttempts();
      localStorage.setItem("savedFullPath", returnTo);

      const casLoginURL = `${config.casLoginBaseURL}?service=${encodeURIComponent(config.serviceURL)}`;
      // console.log('Redirecting to CAS:', casLoginURL);
      window.location = casLoginURL;
    },

    hasValidJWT(botName) {
      const jwt = localStorage.getItem("jwt-" + botName);
      if (!jwt) return false;

      try {
        const decodedJwt = routeGuardUtils.decodeJWT(jwt);
        return !routeGuardUtils.isJwtTokenExpired(decodedJwt);
      } catch {
        return false;
      }
    },

    getLoginAttempts() {
      return parseInt(sessionStorage.getItem('loginAttempts') || '0');
    },

    incrementLoginAttempts() {
      const attempts = this.getLoginAttempts() + 1;
      sessionStorage.setItem('loginAttempts', attempts.toString());
    },

    clearLoginAttempts() {
      sessionStorage.removeItem('loginAttempts');
      localStorage.removeItem('redirectedToCas'); // Legacy cleanup
    },

    handleLoginFailure(message, botName, returnTo) {
      const attempts = this.getLoginAttempts();

      if (attempts >= 2) {
        this.errorMessage = `${message}. Please try again or contact support if the problem persists.`;
        this.clearLoginAttempts();
      } else {
        // Auto-retry
        // console.log(`Login attempt ${attempts + 1} failed, retrying...`);
        this.incrementLoginAttempts();
        setTimeout(() => {
          this.initiateLogin(botName, returnTo);
        }, 1000);
      }
    },

    retryLogin() {
      this.errorMessage = null;
      this.clearLoginAttempts();
      this.handleLogin();
    },

    openInNewWindow() {
      // Escape iframe hell - open bot in new window/tab
      if (this.escapeBotName) {
        const botUrl = `${window.location.origin}${window.location.pathname}#/?botName=${this.escapeBotName}`;
        window.open(botUrl, '_blank', 'noopener,noreferrer');
      }
    }
  }
};
</script>
